import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import PasswordInput from '@/components/PasswordInput';
import { Input } from '@/components/ui/input';
import { Mail, Lock, User, ArrowRight, Sun, Moon, Users, Crown, Chrome, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';
import { API_URL as API } from '@/utils/apiConfig';

// Only allow same-origin, path-only redirects — prevents open-redirect
// vulnerability via ?returnTo=.
function safeReturnTo(raw) {
  if (!raw) return '/app';
  try {
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith('/') || decoded.startsWith('//')) return '/app';
    return decoded;
  } catch {
    return '/app';
  }
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTrial = searchParams.get('trial') === 'true';
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const { t } = useTranslation();

  // Marketing redirect: the "trial=true" deep-link (askeili.com/signup?trial=true)
  // was used in campaigns before the landing-page overhaul. External campaign
  // traffic (no `src` param) is rerouted to the home page so new visitors see
  // the value pitch first. Internal clicks from the landing-page trial banner
  // include `?src=banner` and bypass this redirect. Any `ref` affiliate code
  // is preserved as a query param.
  useEffect(() => {
    const trial = searchParams.get('trial') === 'true';
    const src = searchParams.get('src');
    if (trial && !src) {
      const ref = searchParams.get('ref');
      navigate(ref ? `/?ref=${encodeURIComponent(ref)}` : '/', { replace: true });
    }
  }, [searchParams, navigate]);

  // Capture affiliate referral code from URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('eili_ref_code', ref);
    }
  }, [searchParams]);
  const { colorMode, changeColorMode } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(true);

  // Check if Google login is enabled
  useEffect(() => {
    const checkGoogleLogin = async () => {
      try {
        const res = await axios.get(`${API}/auth/google-login-enabled`);
        setGoogleLoginEnabled(res.data.enabled);
      } catch {
        setGoogleLoginEnabled(true);
      }
    };
    checkGoogleLogin();
  }, []);

  // Auth redirect is handled by AuthGate in App.js — no check needed here

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Store trial flag for AuthCallback to pick up
    if (isTrial) {
      localStorage.setItem('apply_trial_on_signup', 'true');
    }
    const redirectUrl = window.location.origin + returnTo;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${encodeURIComponent('Eili')}`;
  };

  const handleGuestLogin = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/guest`, {}, { withCredentials: true });
      window.location.replace(returnTo);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create guest session';
      toast.error(typeof errorMessage === 'string' ? errorMessage : t('login.failed_guest', 'Could not start guest session'));
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (pw) => {
    if (!pw) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 2) return { level: 1, label: t('password.weak', 'Weak'), color: 'bg-red-500' };
    if (score <= 3) return { level: 2, label: t('password.fair', 'Fair'), color: 'bg-yellow-500' };
    if (score <= 4) return { level: 3, label: t('password.good', 'Good'), color: 'bg-blue-500' };
    return { level: 4, label: t('password.strong', 'Strong'), color: 'bg-green-500' };
  };

  const strength = getPasswordStrength(password);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error(t('signup.accept_terms', 'Please accept the Terms of Use and Privacy Policy'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('signup.passwords_mismatch', 'Passwords do not match'));
      return;
    }
    if (strength.level < 2) {
      toast.error(t('signup.weak_password', 'Please use a stronger password'));
      return;
    }
    setLoading(true);
    try {
      const endpoint = isTrial ? `${API}/auth/register-trial` : `${API}/auth/register`;
      const refCode = localStorage.getItem('eili_ref_code') || undefined;
      await axios.post(endpoint, { email, password, name, username: username.trim() || undefined, website: honeypot || undefined, ref_code: refCode }, { withCredentials: true });
      localStorage.removeItem('eili_ref_code'); // Clear after use
      if (isTrial) toast.success(t('signup.trial_success', 'Welcome! Your 7-day premium trial has started.'));
      // Use replace to prevent back button issues
      window.location.replace(returnTo);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Registration failed';
      toast.error(typeof errorMessage === 'string' ? errorMessage : t('signup.failed_register'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center px-4 py-10" style={{ minHeight: '100dvh', paddingTop: '2.5rem', background: 'var(--theme-background)' }}>
      {/* Mode toggle — top right */}
      <div className="fixed top-4 right-4 z-50" style={{ top: 'max(1rem, var(--safe-area-top, env(safe-area-inset-top, 0px)))' }}>
        <button
          onClick={() => changeColorMode(colorMode === 'dark' ? 'light' : 'dark')}
          className="p-2 rounded-full transition-all"
          style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          data-testid="signup-mode-toggle"
        >
          {colorMode === 'dark'
            ? <Sun className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
            : <Moon className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />}
        </button>
      </div>
      <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid var(--glass-border)', boxShadow: '0 32px 64px rgba(0,0,0,0.15)' }}>
        {/* Logo + title */}
        <div className="text-center pt-10 pb-6 px-8">
          <button onClick={() => navigate('/app')} className="inline-block mb-5 hover:scale-105 transition-transform">
            <img src="/logo.png" alt={t('generated.signuppage_eili_2045')} className="h-20 w-20 mx-auto drop-shadow-lg" />
          </button>
          <h1 className="text-3xl font-bold mb-1.5" style={{ color: 'var(--theme-text)', fontFamily: 'var(--theme-font-family)' }}>{t('signup.title')}</h1>
          <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{t('signup.desc')}</p>
          {isTrial && (
            <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl" style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.25)' }} data-testid="trial-signup-badge">
              <Crown className="h-4 w-4" style={{ color: '#eab308' }} />
              <span className="text-xs font-semibold" style={{ color: '#eab308' }}>{t('generated.signuppage_7_day_premium_2c12')}</span>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="px-8 pb-10">
          <form onSubmit={handleSignup} className="space-y-4">
            {/* Honeypot — invisible to real users, bots auto-fill */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
              aria-hidden="true"
            />
            <div className="relative">
              <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
              <Input
                type="text"
                placeholder={t('signup.name_placeholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="pl-12 py-6 rounded-xl text-sm"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                data-testid="signup-name-input"
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
              <Input
                type="email"
                placeholder={t('login.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-12 py-6 rounded-xl text-sm"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                data-testid="signup-email-input"
              />
            </div>

            <div className="relative">
              <Users className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />
              <Input
                type="text"
                placeholder={t('signup.username_placeholder', 'Username (optional)')}
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="pl-12 py-6 rounded-xl text-sm"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                data-testid="signup-username-input"
                maxLength={30}
              />
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 z-10" style={{ color: 'var(--theme-textSecondary)' }} />
                <PasswordInput
                  InputComponent={Input}
                  placeholder={t('login.password_placeholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-12 py-6 rounded-xl text-sm"
                  style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="signup-password-input"
                />
              </div>
              {password && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--glass-bg-active)' }}>
                      <div
                        className={`h-full rounded-full transition-all ${strength.color}`}
                        style={{ width: `${strength.level * 25}%` }}
                      />
                    </div>
                    <span className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>{strength.label}</span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--theme-textSecondary)' }}>
                    {t('password.requirements', 'Min 8 characters, uppercase, lowercase, and a number')}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 z-10" style={{ color: 'var(--theme-textSecondary)' }} />
              <PasswordInput
                InputComponent={Input}
                placeholder={t('signup.confirm_password', 'Confirm password')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="pl-12 py-6 rounded-xl text-sm"
                style={{ 
                  background: 'var(--glass-bg-active)', 
                  border: confirmPassword && password !== confirmPassword ? '1px solid #ef4444' : '1px solid var(--glass-border)', 
                  color: 'var(--theme-text)' 
                }}
                data-testid="signup-confirm-password-input"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                  {t('signup.passwords_mismatch', 'Passwords do not match')}
                </p>
              )}
              {confirmPassword && password === confirmPassword && confirmPassword.length > 0 && (
                <p className="text-xs mt-1" style={{ color: '#22c55e' }}>
                  {t('signup.passwords_match', 'Passwords match')}
                </p>
              )}
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="terms-checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded accent-indigo-500"
                data-testid="terms-checkbox"
              />
              <span className="text-xs leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('signup.terms_agree', 'I agree to the')}{' '}
                <a href="/terms" target="_blank" className="underline font-medium" style={{ color: 'var(--theme-accent)' }}>
                  {t('signup.terms_link', 'Terms of Use')}
                </a>{' '}
                {t('signup.and', 'and')}{' '}
                <a href="/privacy" target="_blank" className="underline font-medium" style={{ color: 'var(--theme-accent)' }}>
                  {t('signup.privacy_link', 'Privacy Policy')}
                </a>
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2 text-white transition-all hover:opacity-90"
              style={{ background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)` }}
              data-testid="signup-submit-button"
            >
              {t('signup.submit')}
              <ArrowRight className="h-4 w-4" />
            </Button>

            {googleLoginEnabled && (
              <>
                <div className="relative flex items-center my-4">
                  <div className="flex-1 h-px" style={{ background: 'var(--glass-border)' }} />
                  <span className="px-3 text-xs" style={{ color: 'var(--theme-textSecondary)' }}>or</span>
                  <div className="flex-1 h-px" style={{ background: 'var(--glass-border)' }} />
                </div>

                <Button
                  type="button"
                  onClick={handleGoogleSignup}
                  variant="outline"
                  className="w-full py-6 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
                  style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                  data-testid="google-signup-button"
                >
                  <Chrome className="h-5 w-5" />
                  {isTrial ? t('signup.google_trial', 'Sign up with Google & Start Trial') : t('signup.google', 'Sign up with Google')}
                </Button>
              </>
            )}

            <div className="text-center pt-3">
              <button
                type="button"
                onClick={() => navigate(`/login${returnTo !== '/app' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`)}
                className="text-sm transition-colors"
                style={{ color: 'var(--theme-textSecondary)' }}
              >
                {t('login.have_account')} <span className="underline" style={{ color: 'var(--theme-accent)' }}>{t('login.signin')}</span>
              </button>
            </div>

            {/* Explore shortcut — lets visitors browse the app without
                committing to an account. Mirrors the button on the
                login screen so no matter which auth page they land on,
                they can still tap in as a guest. */}
            <div className="pt-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
              <button
                type="button"
                onClick={handleGuestLogin}
                disabled={loading}
                className="w-full py-4 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all"
                style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
                data-testid="signup-guest-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" style={{ color: 'var(--theme-accent)' }} />}
                {t('login.guest', 'Continue as Guest')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
